import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Users, FileText, Target, Award, Calendar, Clock, Activity, PieChart, LineChart, Download, Filter, RefreshCw } from 'lucide-react';
import { formatTimestamp } from '../../utils/timestampUtils';

const AdminAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('overview');

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedTimeframe]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Load analytics data from Firebase
      // This would typically involve aggregating data from multiple collections
      const analyticsData = {
        overview: {
          totalUsers: 0,
          activeUsers: 0,
          totalTests: 0,
          totalQuestions: 0,
          averageScore: 0,
          completionRate: 0
        },
        userGrowth: {
          labels: [],
          data: []
        },
        testPerformance: {
          labels: [],
          data: []
        },
        questionTypes: {
          labels: [],
          data: []
        },
        topPerformers: [],
        recentActivity: []
      };
      
      setAnalyticsData(analyticsData);
    } catch (error) {
      } finally {
      setLoading(false);
    }
  };

  const timeframes = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' }
  ];

  const metrics = [
    { value: 'overview', label: 'Overview', icon: BarChart3 },
    { value: 'users', label: 'User Analytics', icon: Users },
    { value: 'tests', label: 'Test Analytics', icon: FileText },
    { value: 'performance', label: 'Performance', icon: Target },
    { value: 'activity', label: 'Recent Activity', icon: Activity }
  ];

  const getMetricIcon = (metricType) => {
    const metricInfo = metrics.find(m => m.value === metricType);
    return metricInfo ? metricInfo.icon : BarChart3;
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const formatPercentage = (num) => {
    return `${num.toFixed(1)}%`;
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case '_registration': return <Users className="w-4 h-4 text-green-500" />;
      case 'test_completion': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'question_added': return <Target className="w-4 h-4 text-purple-500" />;
      case '_login': return <Activity className="w-4 h-4 text-orange-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityText = (type) => {
    switch (type) {
      case '_registration': return 'Newregistered';
      case 'test_completion': return 'Completed a test';
      case 'question_added': return 'Added new question';
      case '_login': return 'Logged in';
      default: return 'Activity';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Analytics Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Comprehensive insights into system performance andbehavior
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {timeframes.map(timeframe => (
              <option key={timeframe.value} value={timeframe.value}>
                {timeframe.label}
              </option>
            ))}
          </select>
          <button
            onClick={loadAnalyticsData}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Metric Navigation */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex flex-wrap gap-2">
          {metrics.map(metric => {
            const IconComponent = metric.icon;
            return (
              <button
                key={metric.value}
                onClick={() => setSelectedMetric(metric.value)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  selectedMetric === metric.value
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <IconComponent className="w-4 h-4 mr-2" />
                {metric.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics data...</p>
        </div>
      ) : (
        <>
          {/* Overview Metrics */}
          {selectedMetric === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Users</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(analyticsData.overview?.totalUsers || 0)}
                      </p>
                      <p className="text-sm text-green-600 mt-1">+12% from last month</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active Users</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(analyticsData.overview?.activeUsers || 0)}
                      </p>
                      <p className="text-sm text-green-600 mt-1">+8% from last month</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <Activity className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Tests</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(analyticsData.overview?.totalTests || 0)}
                      </p>
                      <p className="text-sm text-green-600 mt-1">+15% from last month</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-full">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white p-6 rounded-lg shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Score</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatPercentage(analyticsData.overview?.averageScore || 0)}
                      </p>
                      <p className="text-sm text-green-600 mt-1">+3% from last month</p>
                    </div>
                    <div className="p-3 bg-orange-100 rounded-full">
                      <Target className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* User Growth Chart */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">User Growth</h3>
                    <button className="text-blue-600 hover:text-blue-800">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="h-64 flex items-end justify-between space-x-2">
                    {analyticsData.Growth?.data.map((value, index) => (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-blue-500 rounded-t"
                          style={{ height: `${(value / Math.max(...analyticsData.Growth.data)) * 200}px` }}
                        ></div>
                        <span className="text-xs text-gray-500 mt-2">
                          {analyticsData.Growth.labels[index]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Test Performance Chart */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Test Performance by Exam</h3>
                    <button className="text-blue-600 hover:text-blue-800">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    {analyticsData.testPerformance?.data.map((score, index) => (
                      <div key={index} className="flex items-center">
                        <span className="text-sm text-gray-600 w-16">
                          {analyticsData.testPerformance.labels[index]}
                        </span>
                        <div className="flex-1 mx-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${score}%` }}
                            ></div>
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-12">
                          {score}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Performers */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
                  <button className="text-blue-600 hover:text-blue-800">
                    View All
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rank
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Average Score
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tests Taken
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Performance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {analyticsData.topPerformers?.map((performer, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {index < 3 ? (
                                <Award className={`w-5 h-5 ${
                                  index === 0 ? 'text-yellow-500' : 
                                  index === 1 ? 'text-gray-400' : 'text-orange-500'
                                }`} />
                              ) : (
                                <span className="text-sm text-gray-500">{index + 1}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {performer.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{performer.score}%</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{performer.tests}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${performer.score}%` }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {selectedMetric === 'activity' && (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                <button className="text-blue-600 hover:text-blue-800">
                  View All
                </button>
              </div>
              <div className="space-y-4">
                {analyticsData.recentActivity?.map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {getActivityIcon(activity.type)}
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">
                        {getActivityText(activity.type)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatTimestamp(activity.timestamp, { includeTime: false })} • {activity.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Question Types Distribution */}
          {selectedMetric === 'tests' && (
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Question Types Distribution</h3>
                <button className="text-blue-600 hover:text-blue-800">
                  <Download className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  {analyticsData.questionTypes?.data.map((percentage, index) => (
                    <div key={index} className="flex items-center">
                      <span className="text-sm text-gray-600 w-20">
                        {analyticsData.questionTypes.labels[index]}
                      </span>
                      <div className="flex-1 mx-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-500 h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12">
                        {percentage}%
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-48 h-48 relative">
                    {/* Placeholder for pie chart */}
                    <div className="w-full h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 flex items-center justify-center">
                      <span className="text-white font-semibold">Pie Chart</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
